import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@radix-ui/react-accordion";
import {
  AlertTriangle,
  ChevronDown,
  GripVertical,
  PencilLine,
  Trash2,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { type FC, type ReactNode, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import { BasicModal, toastError } from "@/components/molecules";
import { useMenuActions } from "@/components/units/BrandFormMenu/menuActions/MenuActionsContext";
import { cn } from "@/lib/utils";
import { rootStore } from "@/store/RootStore";
import { commonIconProps20 } from "@/utils/constants";

import type { BrandFormMenuType } from "../types";
import DialogCategory from "./DialogCategory";

interface Props {
  id: string;
  children: ReactNode;
  categoryNameEn: string;
  categoryNameAr?: string;
  form: UseFormReturn<BrandFormMenuType>;
  hasUpdatePermission?: boolean;
}

const SortableCategoryBlock: FC<Props> = observer(
  ({
    id,
    children,
    categoryNameEn,
    form,
    categoryNameAr,
    hasUpdatePermission = true,
  }) => {
    const menuActions = useMenuActions();
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id, disabled: !hasUpdatePermission });

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const category = (rootStore.menuStore.menu.categories ?? []).find(
      c => c.id === id
    );
    const dishCount = category?.dishes?.length ?? 0;
    const hasDishes = dishCount > 0;
    const isDuplicate = category?.isDuplicate === true;

    const handleDeleteClick = () => {
      setShowDeleteConfirm(true);
    };

    const performDelete = async () => {
      try {
        await menuActions.deleteCategory(id);
        await menuActions.invalidateMenuQuery();
        form.reset(rootStore.menuStore.menu);
        setShowDeleteConfirm(false);
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : "Failed to delete category",
          () => performDelete(),
          "Error"
        );
      }
    };

    return (
      <>
        <div
          ref={setNodeRef}
          style={style}
          className="flex flex-col flex-1 gap-3"
        >
          <AccordionItem value={id} className="border-none group">
            <div className="flex items-center justify-between gap-4 py-2.5 pr-4 pl-1 w-full transition-all duration-200 rounded-md border border-gray-200 group-data-[state=open]:border-yellow-100 group-data-[state=open]:bg-yellow-50/50 group-data-[state=open]:shadow-none">
              <div className="flex min-w-0 w-fit items-center gap-4">
                <button
                  type="button"
                  className="inline-flex shrink-0 cursor-grab touch-none items-center justify-center rounded-md p-2 hover:bg-gray-50 group-data-[state=open]:hover:bg-yellow-100 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  disabled={!hasUpdatePermission}
                  {...attributes}
                  {...listeners}
                  onClick={e => e.stopPropagation()}
                  data-testid={`brand-form-menu-category-drag-${categoryNameEn}`}
                >
                  <GripVertical {...commonIconProps20} color="#AFAFAF" />
                </button>

                <AccordionTrigger asChild>
                  <button
                    type="button"
                    className="flex min-w-0 w-fit flex-1 cursor-pointer items-center justify-between gap-4 bg-transparent p-0 text-left hover:no-underline"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="text-xl font-extrabold"
                        data-testid={`brand-form-menu-category-title-${categoryNameEn}`}
                      >
                        {categoryNameEn}
                      </span>
                      <ChevronDown className="w-5 h-5 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />

                      {isDuplicate && (
                        <span
                          className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-md px-2 py-0.5"
                          data-testid={`brand-form-menu-category-duplicate-${categoryNameEn}`}
                          title="Duplicate category name — please rename before saving"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          Duplicate name
                        </span>
                      )}
                    </span>
                  </button>
                </AccordionTrigger>

                <DialogCategory
                  form={form}
                  nameSelect="categories"
                  disabled={false}
                  editId={id}
                  initialNameEn={categoryNameEn}
                  initialNameAr={categoryNameAr}
                  trigger={
                    <button
                      type="button"
                      onClick={e => e.stopPropagation()}
                      disabled={!hasUpdatePermission}
                      className={cn(
                        "shrink-0 p-1 hover:bg-gray-100 rounded transition-colors group-data-[state=open]:hover:bg-yellow-100 transition-all hover:text-gray-600 duration-200 hover:cursor-pointer disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      )}
                    >
                      {hasUpdatePermission && (
                        <PencilLine className="text-gray-400 w-4 h-4" />
                      )}
                    </button>
                  }
                />
              </div>

              {hasUpdatePermission && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteClick();
                    }}
                    disabled={!hasUpdatePermission}
                    className="flex items-center gap-2 font-bold text-sm px-4 py-2 hover:bg-gray-50 rounded-md transition-colors group-data-[state=open]:hover:bg-yellow-100 hover:cursor-pointer"
                  >
                    <Trash2 className="text-gray-500 w-4 h-4" />
                    <span className="text-gray-500">Remove</span>
                  </button>
                </div>
              )}
            </div>

            <AccordionContent className="pt-4">{children}</AccordionContent>
          </AccordionItem>
        </div>

        {showDeleteConfirm && (
          <BasicModal
            isOpen={showDeleteConfirm}
            onOpenChange={setShowDeleteConfirm}
            title="Delete Category"
            btnTitle="Delete"
            action={performDelete}
            body={
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-500">
                  {hasDishes ? (
                    <>
                      Category
                      <strong className="text-gray-800 px-1">
                        &quot;{categoryNameEn}&quot;
                      </strong>
                      contains
                      <strong className="text-gray-800 pl-1">
                        {dishCount} dish{dishCount !== 1 ? "es" : ""}
                      </strong>
                      . Removing this category will permanently delete all
                      dishes inside it. This action cannot be undone.
                    </>
                  ) : (
                    <>
                      This will permanently delete category
                      <strong className="text-gray-800 px-1">
                        &quot;{categoryNameEn}&quot;
                      </strong>
                      . This action cannot be undone.
                    </>
                  )}
                </p>
              </div>
            }
          />
        )}
      </>
    );
  }
);

export default SortableCategoryBlock;
